import React from 'react'
import ReactDOM from 'react-dom/client'
import { App } from './App'
import './styles/globals.css'
import { initializeServiceWorker } from './utils/serviceWorker'

// Initialize Service Worker for PWA functionality
if (import.meta.env.PROD) {
  let updateAvailable = false
  let registration: ServiceWorkerRegistration | null = null
  
  initializeServiceWorker({
    onUpdate: (reg) => {
      console.log('🔄 App update available')
      registration = reg
      updateAvailable = true
      
      // Create a subtle notification instead of intrusive confirm
      const updateBanner = document.createElement('div')
      updateBanner.id = 'update-banner'
      updateBanner.innerHTML = `
        <div style="
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: #1e40af;
          color: white;
          padding: 12px;
          text-align: center;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 14px;
          z-index: 9999;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        ">
          <span>📱 New version available!</span>
          <button id="update-btn" style="
            background: white;
            color: #1e40af;
            border: none;
            padding: 4px 12px;
            margin-left: 12px;
            border-radius: 4px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
          ">Update Now</button>
          <button id="dismiss-btn" style="
            background: transparent;
            color: white;
            border: 1px solid rgba(255,255,255,0.3);
            padding: 4px 12px;
            margin-left: 8px;
            border-radius: 4px;
            font-size: 13px;
            cursor: pointer;
          ">Later</button>
        </div>
      `
      
      // Add to page
      document.body.appendChild(updateBanner)
      
      // Handle update button
      document.getElementById('update-btn')?.addEventListener('click', () => {
        if (registration?.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' })
          updateBanner.remove()
          
          // Show updating message
          const updatingMsg = document.createElement('div')
          updatingMsg.innerHTML = `
            <div style="
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              background: #059669;
              color: white;
              padding: 12px;
              text-align: center;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              font-size: 14px;
              z-index: 9999;
            ">
              ⏳ Updating app... Please wait
            </div>
          `
          document.body.appendChild(updatingMsg)
          
          // Remove after 3 seconds or on reload
          setTimeout(() => updatingMsg.remove(), 3000)
        }
      })
      
      // Handle dismiss button
      document.getElementById('dismiss-btn')?.addEventListener('click', () => {
        updateBanner.remove()
        updateAvailable = false
      })
      
      // Auto-hide after 30 seconds
      setTimeout(() => {
        if (document.getElementById('update-banner')) {
          updateBanner.remove()
          updateAvailable = false
        }
      }, 30000)
    },
    onSuccess: () => {
      console.log('✅ App is ready for offline use')
    },
    onError: (error) => {
      console.error('❌ Service worker registration failed:', error)
    },
    onOffline: () => {
      console.log('📴 App is now offline')
      // Show offline indicator
    },
    onOnline: () => {
      console.log('🌐 App is back online')
      // Hide offline indicator and sync data
    }
  })
}

// Database functions available in development console:
if (import.meta.env.DEV) {
  // Database is already seeded via npm scripts - these are optional dev utilities
  console.log('🔧 Development utilities:')
  console.log('  - Database already seeded with npm run seed:optimized')
  console.log('  - Use Firebase Console to view data')
  console.log('  - For re-seeding, run: npm run seed:optimized')
  
  // Notification testing utilities (only load if available)
  import('./utils/notificationTestUtils').then(() => {
    console.log('🔔 Notification testing utilities loaded!')
  }).catch(() => {
    // Silently fail - not critical
  })
  
  // Load notification debugger
  import('./utils/notificationDebugger').then(() => {
    console.log('🔧 Notification debugger loaded!')
  }).catch(() => {
    // Silently fail - not critical
  })
  
  // Load local FCM tester
  import('./utils/localFCMTester').then(() => {
    console.log('🧪 Local FCM tester loaded!')
  }).catch(() => {
    // Silently fail - not critical
  })
  
  // Load FCM debugger
  import('./utils/fcmDebugger').then(() => {
    console.log('🔧 FCM debugger loaded!')
  }).catch(() => {
    // Silently fail - not critical
  })
  
  // Load scheduled notifications debugger
  import('./utils/scheduledNotificationsDebugger').then(() => {
    console.log('📅 Scheduled notifications debugger loaded!')
  }).catch(() => {
    // Silently fail - not critical
  })
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)